import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Popper,
  type PopperProps,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import type { FC, ReactElement, ReactNode } from 'react';

import { InputTooltip } from '../InputTooltip/InputTooltip';

const ContentWidthPopper: FC<PopperProps> = ({ style, ...props }) => (
  <Popper
    {...props}
    placement='bottom-start'
    style={{ ...style, width: 'auto' }}
    sx={{ minWidth: style?.width, maxWidth: '25rem' }}
  />
);

export type MultiAutocompleteOption<T> = {
  value: T;
  label: string;
  sublabels?: string[];
  isDisabled?: boolean;
};

type Props<OptionValueType> = {
  label?: string;
  name?: string;
  options: MultiAutocompleteOption<OptionValueType>[];
  value: MultiAutocompleteOption<OptionValueType>[];
  onChange: (selectedOptions: MultiAutocompleteOption<OptionValueType>[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  /** Helper text to display below the input */
  helperText?: string;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  /** if true - the options will be displayed with checkbox */
  withCheckbox?: boolean;
  /** custom render function for the value of the input. */
  renderValue?: (selected: MultiAutocompleteOption<OptionValueType>[]) => ReactNode;
  /** Fired whenever the user types; combine with `inputValue` for server-side searches. */
  onInputValueChange?: (value: string) => void;
  /** Controlled text value shown in the autocomplete input. */
  inputValue?: string;
  /** Use if options are filtered elsewhere, e.g. by the API on `inputValue`. */
  disableClientSideFiltering?: boolean;
};

export const MultiAutocomplete = <OptionValueType,>({
  label,
  name,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  loading,
  error,
  helperText,
  tip,
  withCheckbox,
  renderValue,
  onInputValueChange,
  inputValue,
  disableClientSideFiltering,
}: Props<OptionValueType>): ReactElement => {
  const hasError = error === true;
  const filterByLabel = createFilterOptions<MultiAutocompleteOption<OptionValueType>>({
    matchFrom: 'any',
    stringify: (option) => option.label,
  });

  const labelWithTip =
    label === undefined ? null : (
      <Box display='flex' gap='0.25rem' alignItems='center'>
        {label}
        {tip !== undefined ? <InputTooltip tip={tip} /> : null}
      </Box>
    );

  return (
    <Autocomplete
      disableCloseOnSelect={withCheckbox === true}
      multiple
      id={name}
      onBlur={onBlur}
      disabled={disabled}
      loading={loading}
      loadingText='Loading...'
      isOptionEqualToValue={(option, val): boolean =>
        val === undefined || option.value === val.value
      }
      value={value}
      inputValue={inputValue}
      onInputChange={(_: unknown, newInputValue): void => {
        onInputValueChange?.(newInputValue);
      }}
      freeSolo
      forcePopupIcon
      onChange={(
        _: unknown,
        selectedOpts: (string | MultiAutocompleteOption<OptionValueType>)[],
      ): void => {
        onChange(selectedOpts.filter((opt) => typeof opt !== 'string'));
      }}
      autoComplete={false}
      fullWidth
      PopperComponent={ContentWidthPopper}
      options={loading === true ? [] : options}
      renderTags={renderValue}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          name={name}
          error={hasError}
          helperText={helperText}
          placeholder={placeholder}
          label={labelWithTip}
          // Close outlined box + fix height if label is undefined
          sx={{
            ...(label === undefined && {
              fieldset: { top: 0 },
              legend: { display: 'none' },
            }),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading === true ? <CircularProgress color='inherit' size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      // Identity rather than undefined, which falls back to MUI's default filtering and would
      // drop a server-added "N more options" row
      filterOptions={
        disableClientSideFiltering === true
          ? (a): MultiAutocompleteOption<OptionValueType>[] => a
          : filterByLabel
      }
      getOptionDisabled={(option): boolean => option.isDisabled === true}
      renderOption={(params, option, { selected }): ReactElement => (
        <Box {...params} key={`${name}-options-${option.value}`} component='li'>
          {withCheckbox === true && <Checkbox sx={{ marginRight: '0.5rem' }} checked={selected} />}
          <Box
            display='flex'
            flexDirection='column'
            alignItems='start !important'
            padding='0rem'
            whiteSpace='nowrap'
            minWidth={0}
            overflow='hidden'
          >
            <Typography noWrap maxWidth='100%' title={option.label}>
              {option.label}
            </Typography>

            {option.sublabels !== undefined && option.sublabels.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {option.sublabels
                  .filter((sl) => sl !== '')
                  .map((sublabel, subLabelIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Typography key={`${sublabel}-${subLabelIndex}`} variant='body3'>
                      {sublabel}
                    </Typography>
                  ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    />
  );
};
